// src/pages/BookingPage.js

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Button,
  Avatar,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format, parse, addMinutes, isBefore, isAfter } from 'date-fns';
import { enUS } from 'date-fns/locale';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { db } from '../firebaseConfig'; // deine Firebase-Config
import { collection, query, where, addDoc, getDocs, doc, getDoc } from 'firebase/firestore';

import { useTenant } from '../contexts/TenantContext';

// Utils
import { loadAppointmentTypesFromDB, loadEmployeesFromDB, fetchDataForDate } from '../utils/firebaseQueries';
import { fetchAvailableDaysForMonth } from '../utils/fetchAvailableDaysForMonth';
import { generateICSFile, roundToNextHalfHour } from '../utils/calendarUtils';

// Components
import EmployeeSelection from '../components/BookingPage/EmployeeSelection';
import AppointmentTypeSelection from '../components/BookingPage/AppointmentTypeSelection';
import DateSelection from '../components/BookingPage/DateSelection';
import AvailableTimes from '../components/BookingPage/AvailableTimes';
import CustomerForm from '../components/BookingPage/CustomerForm';
import SnackbarNotification from '../components/common/SnackbarNotification';
import { createActivityLog, buildActivityText } from '../utils/adminAppointments';

// Icons
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SEND_APPOINTMENT_CONFIRMATION_URL =
  process.env.REACT_APP_SEND_APPOINTMENT_CONFIRMATION_URL ||
  'https://sendappointmentconfirmation-ce72zbmxaq-uc.a.run.app';

export default function BookingPage() {
  const { tenantId } = useTenant();
  const [step, setStep] = useState(0);

  // DB-Daten (Terminarten, Mitarbeiter)
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Gewählte Werte
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedAppointmentType, setSelectedAppointmentType] = useState('');

  // Step 1 (Datum)
  const [availableDays, setAvailableDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  // Step 2 (Uhrzeit)
  const [availableStartTimes, setAvailableStartTimes] = useState([]);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [loadingTimes, setLoadingTimes] = useState(false);

  // Step 3 (Kundendaten)
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState('');

  // Buchungserfolg
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [calendarLink, setCalendarLink] = useState(null);

  // Unternehmensdaten
  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ----------------------------------
  // 1) Terminarten + Mitarbeiter laden
  // ----------------------------------
  useEffect(() => {
    const loadData = async () => {
      const [types, emps] = await Promise.all([
        loadAppointmentTypesFromDB(tenantId),
        loadEmployeesFromDB(tenantId),
      ]);
      setAppointmentTypes(types);
      setEmployees(emps);
    };
    loadData();
  }, [tenantId]);

  // ----------------------------------
  // 2) Unternehmensdaten laden
  // ----------------------------------
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!tenantId) return;
      try {
        const tenantDocRef = doc(db, 'tenants', tenantId);
        const tenantSnap = await getDoc(tenantDocRef);
        if (tenantSnap.exists()) {
          const tenantData = tenantSnap.data();
          setCompanyName(tenantData.companyName || '');
          setLogoUrl(tenantData.logoUrl || '');
        }
      } catch (err) {
        console.error('Fehler beim Abrufen der Unternehmensdaten:', err);
      }
    };
    fetchCompanyData();
  }, [tenantId]);

  // ----------------------------------
  // 3) Monatliche Abfrage der verfügbaren Tage
  //    -> wird in DateSelection via onMonthChange oder initial ausgeführt
  // ----------------------------------

  // Wir nutzen hier noch ein kleines Cache, damit wir nicht den gleichen Monat mehrfach laden müssen
  const [monthCache, setMonthCache] = useState({});
  const fetchAvailableDaysForDisplayedMonth = async (monthDate) => {
    if (!selectedEmployee || !selectedAppointmentType) {
      setAvailableDays([]);
      return;
    }

    // Rufe unsere Hilfsfunktion auf, die alle relevanten Daten in einer Abfrage für den Monat holt
    const days = await fetchAvailableDaysForMonth(
      tenantId,
      monthDate,
      selectedAppointmentType,
      selectedEmployee,
      appointmentTypes,
      employees,
      monthCache,
      setMonthCache
    );
    setAvailableDays(days);
  };

  // ----------------------------------
  // 4) Step 2: Uhrzeiten laden, sobald Step=2
  // ----------------------------------
  useEffect(() => {
    const loadTimes = async () => {
      if (step !== 2) return;
      if (!selectedEmployee || !selectedAppointmentType || !selectedDate) return;

      setLoadingTimes(true);
      try {
        const typeObj = appointmentTypes.find((t) => t.id === selectedAppointmentType);
        const duration = typeObj ? typeObj.duration : 60;

        const { availabilities, absences, appointments } = await fetchDataForDate(
          tenantId,
          selectedDate,
          selectedAppointmentType,
          selectedEmployee
        );

        const now = roundToNextHalfHour(new Date());
        const dayOfWeek = format(selectedDate, 'EEEE', { locale: enUS });
        let startTimes = [];

        for (const availability of availabilities) {
          const empId = availability.employeeId;
          // Filter Terminart
          if (typeObj && !typeObj.assignedEmployees.includes(empId)) {
            continue;
          }
          // Filter Mitarbeiter
          if (empId !== selectedEmployee) {
            continue;
          }
          // Abwesenheit
          if (absences.some((a) => a.employeeId === empId)) {
            continue;
          }
          // Wochentag
          if (!availability.weekdays.includes(dayOfWeek)) {
            continue;
          }

          const availabilityStart = parse(availability.startTime, 'HH:mm', new Date(selectedDate));
          const availabilityEnd = parse(availability.endTime, 'HH:mm', new Date(selectedDate));

          let freeSegments = [];
          // Wenn Datum == heute => ab jetzt
          if (format(selectedDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd')) {
            if (isBefore(availabilityEnd, now)) {
              continue;
            }
            freeSegments = [
              {
                start: isAfter(availabilityStart, now) ? availabilityStart : now,
                end: availabilityEnd,
              },
            ];
          } else {
            freeSegments = [{ start: availabilityStart, end: availabilityEnd }];
          }

          // Vorhandene Termine subtrahieren
          const empAppointments = appointments.filter((a) => a.employeeId === empId);
          for (const app of empAppointments) {
            const appStart = parse(app.startTime, 'HH:mm', new Date(selectedDate));
            const appEnd = addMinutes(appStart, app.duration);
            freeSegments = freeSegments.flatMap((seg) => {
              if (isBefore(seg.end, appStart) || isAfter(seg.start, appEnd)) {
                return [seg];
              }
              const newSegs = [];
              if (isBefore(seg.start, appStart)) {
                newSegs.push({ start: seg.start, end: appStart });
              }
              if (isAfter(seg.end, appEnd)) {
                newSegs.push({ start: appEnd, end: seg.end });
              }
              return newSegs;
            });
          }

          // 30-min-Slots
          for (const seg of freeSegments) {
            let slotStart = seg.start;
            while (addMinutes(slotStart, duration) <= seg.end) {
              startTimes.push({
                employeeId: empId,
                employeeName: employees.find((e) => e.id === empId)?.name || 'Unbekannt',
                startTime: format(slotStart, 'HH:mm'),
              });
              slotStart = addMinutes(slotStart, 30);
            }
          }
        }

        // unique
        const uniqueTimes = Array.from(new Set(startTimes.map((s) => `${s.employeeId}-${s.startTime}`))).map(
          (key) => {
            const [empId, st] = key.split('-');
            return {
              employeeId: empId,
              employeeName: employees.find((e) => e.id === empId)?.name || 'Unbekannt',
              startTime: st,
            };
          }
        );
        setAvailableStartTimes(uniqueTimes);
      } finally {
        setLoadingTimes(false);
      }
    };

    loadTimes();
  }, [step, selectedEmployee, selectedAppointmentType, selectedDate, tenantId, appointmentTypes, employees]);

  // ----------------------------------
  // 5) Buchen (Step 3)
  // ----------------------------------
  const handleBooking = async () => {
    if (
      !selectedEmployee ||
      !selectedAppointmentType ||
      !selectedDate ||
      !selectedStartTime ||
      !customerName ||
      !customerEmail ||
      !customerPhoneNumber
    ) {
      showSnackbar('Bitte alle Felder ausfüllen!', 'error');
      return;
    }

    try {
      // Check, ob Termin schon belegt
      const existingQuery = query(
        collection(db, 'tenants', tenantId, 'appointments'),
        where('date', '==', format(selectedDate, 'yyyy-MM-dd')),
        where('employeeId', '==', selectedEmployee),
        where('startTime', '==', selectedStartTime)
      );
      const existingSnap = await getDocs(existingQuery);
      if (!existingSnap.empty) {
        alert('Dieser Termin ist bereits gebucht. Bitte wähle eine andere Uhrzeit.');
        return;
      }

      const typeObj = appointmentTypes.find((t) => t.id === selectedAppointmentType);
      const duration = typeObj?.duration || 60;
      const appointmentTypeName = typeObj?.name || 'Unbekannt';
      const employeeName = employees.find((e) => e.id === selectedEmployee)?.name || 'Unbekannt';

      const startDateTime = parse(
        `${format(selectedDate, 'yyyy-MM-dd')} ${selectedStartTime}`,
        'yyyy-MM-dd HH:mm',
        new Date()
      );
      const endDateTime = addMinutes(startDateTime, duration);
      const cancelToken = uuidv4();

      // In DB eintragen
      const appointmentRef = await addDoc(collection(db, 'tenants', tenantId, 'appointments'), {
        date: format(selectedDate, 'yyyy-MM-dd'),
        startTime: selectedStartTime,
        duration,
        employeeId: selectedEmployee,
        appointmentTypeId: selectedAppointmentType,
        customerName,
        customerEmail,
        customerPhoneNumber,
        canceled: false,
        cancelToken,
        bookedAt: new Date(),
        bookingSource: 'online',
      });

      try {
        await createActivityLog(tenantId, {
          type: 'appointment_created',
          appointmentId: appointmentRef.id,
          appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
          startTime: selectedStartTime,
          customerName,
          employeeId: selectedEmployee,
          appointmentTypeId: selectedAppointmentType,
          message: buildActivityText({
            type: 'appointment_created',
            customerName,
            appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
            startTime: selectedStartTime,
          }),
        });
      } catch (activityError) {
        console.warn('Aktivitätslog konnte nicht geschrieben werden:', activityError);
      }

      // ICS-Datei
      const icsFile = generateICSFile({
        title: `Termin: ${appointmentTypeName}`,
        description: `Geplant mit ${employeeName}`,
        location: 'Vor Ort oder Online',
        startDateTime,
        endDateTime,
        cancellationLink: `https://${tenantId}.terminfluss.de/cancel?tenantId=${tenantId}&token=${cancelToken}`,
      });
      setCalendarLink(icsFile);

      showSnackbar('Termin erfolgreich gebucht!', 'success');
      setBookingSuccess(true);

      // E-Mail (Cloud Function)
      await axios.post(SEND_APPOINTMENT_CONFIRMATION_URL, {
        tenantId,
        customerEmail,
        customerName,
        appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
        startTime: selectedStartTime,
        duration,
        appointmentTypeName,
        employeeName,
        cancellationLink: `https://${tenantId}.terminfluss.de/cancel?tenantId=${tenantId}&token=${cancelToken}`,
      });
    } catch (err) {
      console.error('Fehler beim Buchen:', err);
      showSnackbar('Fehler beim Buchen des Termins. Bitte versuche es erneut.', 'error');
    }
  };

  // --------------------------
  // Step-Navigation
  // --------------------------
  const canGoNext = () => {
    if (bookingSuccess) return false;
    switch (step) {
      case 0:
        // Mitarbeiter + Terminart müssen beide gewählt sein
        return !!selectedEmployee && !!selectedAppointmentType;
      case 1:
        // Datum muss gewählt sein
        return !!selectedDate;
      case 2:
        // Uhrzeit muss gewählt sein
        return !!selectedStartTime;
      case 3:
        // Klick auf "Jetzt Buchen"
        return true;
      default:
        return false;
    }
  };

  const goNext = () => {
    if (step === 3) {
      handleBooking();
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const goBack = () => {
    if (step > 0 && !bookingSuccess) {
      setStep((prev) => prev - 1);
    }
  };

  // Alles neu starten
  const resetBooking = () => {
    setSelectedEmployee('');
    setSelectedAppointmentType('');
    setSelectedDate(null);
    setSelectedStartTime('');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhoneNumber('');
    setBookingSuccess(false);
    setCalendarLink(null);
    setStep(0);
  };

  // Optional: Nur Step 0 zurücksetzen
  const resetStep0 = () => {
    setSelectedEmployee('');
    setSelectedAppointmentType('');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 12, position: 'relative' }}>
      {/* Header mit Firmenlogo und -name */}
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar disableGutters>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {logoUrl ? (
                <Avatar
                  src={logoUrl}
                  alt="Unternehmenslogo"
                  sx={{ width: isMobile ? 40 : 60, height: isMobile ? 40 : 60, mr: 2 }}
                  variant="square"
                />
              ) : (
                <Avatar
                  sx={{
                    width: isMobile ? 40 : 60,
                    height: isMobile ? 40 : 60,
                    bgcolor: 'grey.300',
                    mr: 2,
                    fontSize: isMobile ? 20 : 24,
                  }}
                  variant="square"
                >
                  U
                </Avatar>
              )}
              <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" color="#333">
                {companyName || 'Ihr Unternehmen'}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hauptinhalt */}
      <Paper sx={{ p: 4, mt: 2, borderRadius: 2, boxShadow: 3 }}>
        <Typography variant="h4" align="center" gutterBottom color="#555">
          Termin buchen
        </Typography>

        {/* Schrittanzeige */}
        {!bookingSuccess ? (
          <Typography variant="body1" align="center" gutterBottom sx={{ mb: 3, color: '#777' }}>
            Schritt {step + 1} von 4
          </Typography>
        ) : (
          <Typography variant="body1" align="center" gutterBottom sx={{ mb: 3, color: 'green' }}>
            Buchung erfolgreich!
          </Typography>
        )}

        {/* STEP 0: Mitarbeiter & Terminart */}
        {step === 0 && !bookingSuccess && (
          <>
            <Typography variant="h6" gutterBottom color="#555">
              Mitarbeiter & Terminart
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <EmployeeSelection
                  employees={employees}
                  selectedEmployee={selectedEmployee}
                  setSelectedEmployee={setSelectedEmployee}
                  selectedAppointmentType={selectedAppointmentType}
                  appointmentTypes={appointmentTypes}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppointmentTypeSelection
                  appointmentTypes={appointmentTypes}
                  selectedAppointmentType={selectedAppointmentType}
                  setSelectedAppointmentType={setSelectedAppointmentType}
                  selectedEmployee={selectedEmployee}
                />
              </Grid>
            </Grid>
            {(selectedEmployee || selectedAppointmentType) && (
              <Box mt={2}>
                <Button variant="outlined" onClick={resetStep0} color="secondary">
                  Auswahl zurücksetzen
                </Button>
              </Box>
            )}
          </>
        )}

        {/* STEP 1: Datum */}
        {step === 1 && !bookingSuccess && (
          <>
            <Typography variant="h6" gutterBottom color="#555">
              Datum wählen
            </Typography>
            <DateSelection
              tenantId={tenantId}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              availableDays={availableDays}
              fetchAvailableDaysForDisplayedMonth={fetchAvailableDaysForDisplayedMonth}
              selectedEmployee={selectedEmployee}
              selectedAppointmentType={selectedAppointmentType}
              appointmentTypes={appointmentTypes}
              employees={employees}
            />
          </>
        )}

        {/* STEP 2: Uhrzeit (Termin) */}
        {step === 2 && !bookingSuccess && (
          <>
            <Typography variant="h6" gutterBottom color="#555">
              Uhrzeit (Termin)
            </Typography>
            <AvailableTimes
              loading={loadingTimes}
              availableStartTimes={availableStartTimes}
              selectedStartTime={selectedStartTime}
              selectedEmployee={selectedEmployee}
              setSelectedStartTime={setSelectedStartTime}
              setSelectedEmployee={setSelectedEmployee}
            />
          </>
        )}

        {/* STEP 3: Kundendaten & Buchen */}
        {step === 3 && !bookingSuccess && (
          <>
            <Typography variant="h6" gutterBottom color="#555">
              Kundendaten
            </Typography>
            <CustomerForm
              customerName={customerName}
              setCustomerName={setCustomerName}
              customerEmail={customerEmail}
              setCustomerEmail={setCustomerEmail}
              customerPhoneNumber={customerPhoneNumber}
              setCustomerPhoneNumber={setCustomerPhoneNumber}
            />
          </>
        )}

        {/* Buchungserfolg */}
        {bookingSuccess && (
          <Box textAlign="center">
            <Typography variant="h5" gutterBottom color="green">
              Ihr Termin wurde erfolgreich gebucht!
            </Typography>
            {calendarLink && (
              <Box sx={{ mt: 2 }}>
                <a href={calendarLink} download="Termin.ics" style={{ textDecoration: 'none' }}>
                  <Button variant="outlined" color="secondary">
                    In den Kalender hinzufügen
                  </Button>
                </a>
              </Box>
            )}
            <Box sx={{ mt: 4 }}>
              <Button variant="contained" color="primary" onClick={resetBooking}>
                Neuen Termin buchen
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Footer Navigation (Zurück / Weiter) */}
      {!bookingSuccess && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '32px', // Platz für den Footer reservieren
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderTop: '1px solid #ccc',
            p: 2,
            boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            variant="text"
            disabled={step === 0}
            onClick={goBack}
            color="secondary"
            startIcon={<ArrowBackIcon />}
          >
            Zurück
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={goNext}
            disabled={!canGoNext()}
            endIcon={step < 3 ? <ArrowForwardIcon /> : null}
          >
            {step < 3 ? 'Weiter' : 'Jetzt buchen'}
          </Button>
        </Box>
      )}

      <SnackbarNotification snackbar={snackbar} handleCloseSnackbar={handleCloseSnackbar} />
    </Container>
  );
}
